

import { Card } from './data/cards';
import { getCurrentHero } from './gameState';
import { CombatEvent } from './combatEngine';
import type { Hero } from './data/heroes';

/**
 * Cost of a shop refresh after hero powers. Don Promotor
 * (passive_on_refresh with no buff config) makes the first refresh each
 * turn free.
 */
export function refreshCostForHero(
  hero: Hero | null,
  baseCost: number,
  refreshCountThisTurn: number,
): number {
  // NOTE: this used to hard-code `hero.id === 'local_promoter'` (Don Promotor,
  // "first refresh each turn is free"). That hero was re-rolled into El del
  // Apagon in the bank/burn pass, and keying a perk to an ID meant the new
  // hero silently inherited a discount his own card never claimed. If a
  // free-refresh hero comes back, give it its own powerType.
  void refreshCountThisTurn;
  return baseCost;
}

/**
 * Cost of a tavern upgrade after hero powers. La Patrona del Swapmeet
 * (passive_tier_rush) is the RUSH lane's engine — a flat discount that makes
 * tiering up every other turn affordable instead of a one-time gamble.
 *
 * Never returns below 0.
 */
export function tierUpCostForHero(hero: Hero | null, baseCost: number): number {
  if (hero?.powerType !== 'passive_tier_rush') return baseCost;
  const discount = hero.powerConfig.discount || 0;
  return Math.max(0, baseCost - discount);
}

/**
 * Whether this hero hands the player a free unit of the tier they just
 * unlocked. The other half of the RUSH loop: reaching T5 on turn 6 only
 * feels powerful if you can actually field something from T5 that turn.
 */
export function grantsUnitOnTierUp(hero: Hero | null): boolean {
  return (
    hero?.powerType === 'passive_tier_rush' &&
    hero.powerConfig.grantUnitOfNewTier === true
  );
}

/**
 * Applies passive_on_refresh shop effects (El Profe de la Tocada /
 * La DJ del Toquín: a random unit in the freshly-rolled shop gets
 * +attack/+health). Returns a new shop array; input is not mutated.
 */
export function applyOnRefreshHeroPower(
  hero: Hero | null,
  shop: Card[],
  rng: () => number = Math.random,
): Card[] {
  if (!hero || hero.powerType !== 'passive_on_refresh') return shop;
  const attack = hero.powerConfig.attack || 0;
  const health = hero.powerConfig.health || 0;
  if ((attack <= 0 && health <= 0) || shop.length === 0) return shop;

  const idx = Math.floor(rng() * shop.length);
  const next = [...shop];
  next[idx] = {
    ...next[idx],
    attack: next[idx].attack + attack,
    health: next[idx].health + health,
  };
  return next;
}

/**
 * Applies any relevant pre-combat passive abilities from the player's hero to the board.
 * NOTE: This function mutates the playerBoard array that is passed to it.
 * @param playerBoard The player's board, copied for combat simulation.
 * @returns An array of CombatEvent objects representing the effects that were applied.
 */
export function applyPreCombatHeroPassives(playerBoard: Card[]): CombatEvent[] {
  const hero = getCurrentHero();
  const events: CombatEvent[] = [];

  if (!hero) {
    return events;
  }

  // Use a switch statement to handle different passive power types.
  switch (hero.powerType) {
    case "passive_frontline_health": {
      const healthBonus = hero.powerConfig.health || 0;
      const unitCount = hero.powerConfig.count || 0;
      const affectedUnitIds: string[] = [];

      // Apply the health bonus to the leftmost 'unitCount' units.
      for (let i = 0; i < unitCount && i < playerBoard.length; i++) {
        const unit = playerBoard[i];
        if (unit) {
          unit.health += healthBonus;
          affectedUnitIds.push(unit.instanceId!);
        }
      }

      if (affectedUnitIds.length > 0) {
        events.push({
          type: "hero_effect",
          effectId: "frontline_health_buff",
          details: { unitIds: affectedUnitIds, amount: healthBonus }
        });
      }
      break;
    }

    case "passive_first_strike": {
      // Apply the first strike flag to the leftmost unit.
      if (playerBoard.length > 0) {
        const unit = playerBoard[0];
        if (unit) {
          if (!unit.flags) {
            unit.flags = {};
          }
          unit.flags.firstStrike = true;
          events.push({
            type: "hero_effect",
            effectId: "first_strike",
            details: { unitId: unit.instanceId! }
          });
        }
      }
      break;
    }

    case "passive_stat_swap": {
      // Swap Attack and Health of the rightmost unit.
      if (playerBoard.length > 0) {
        const unit = playerBoard[playerBoard.length - 1];
        if (unit) {
          const tempAttack = unit.attack;
          unit.attack = unit.health;
          unit.health = tempAttack;
          events.push({
            type: "hero_effect",
            effectId: "stat_swap",
            details: { unitId: unit.instanceId!, attack: unit.attack, health: unit.health }
          });
        }
      }
      break;
    }

    case "passive_start_of_combat_scout": {
      // Gain +1 Attack for each enemy unit (this requires enemy board info)
      // This will be handled in combatEngine with enemy board context
      break;
    }

    case "passive_start_of_combat_backline": {
      // La Ultima Rola: the LAST unit in line swings huge.
      // This mutates the combat copy of the board, which is what makes the
      // buff inherently "this combat only" — nothing is written back to the
      // tavern board, so the surge dies with the set.
      const bonus = hero.powerConfig.attack || 0;
      const target = playerBoard[playerBoard.length - 1];
      if (target && bonus > 0) {
        target.attack += bonus;
        events.push({
          type: "hero_effect",
          effectId: "start_of_combat_backline",
          details: { targetId: target.instanceId!, attack: bonus },
        });
      }
      break;
    }
    case "passive_tribe_surge": {
      // La Duena del Foro: the roster's tribe payoff. Counts heads per tribe
      // on the board that is about to fight and pays every unit of the
      // largest tribe that clears the threshold. Untribed (neutral) units are
      // not a tribe and never count — the whole point is rewarding a
      // committed draft, and neutrals are what you take when you did not
      // commit. Raw headcount, not the weighted `countTribe`: a hero
      // threshold the player has to eyeball should be readable off the board.
      const threshold = hero.powerConfig.tribeThreshold ?? 0;
      const bonus = hero.powerConfig.attack || 0;
      if (threshold > 0 && bonus > 0) {
        const counts = new Map<string, number>();
        for (const unit of playerBoard) {
          if (!unit?.tribe) continue;
          counts.set(unit.tribe, (counts.get(unit.tribe) ?? 0) + 1);
        }
        let best: string | null = null;
        for (const [tribe, n] of counts) {
          if (n >= threshold && (best === null || n > counts.get(best)!)) best = tribe;
        }
        if (best) {
          const affectedUnitIds: string[] = [];
          for (const unit of playerBoard) {
            if (unit?.tribe !== best) continue;
            unit.attack += bonus;
            affectedUnitIds.push(unit.instanceId!);
          }
          events.push({
            type: "hero_effect",
            effectId: "tribe_surge",
            details: { unitIds: affectedUnitIds, amount: bonus, tribe: best },
          });
        }
      }
      break;
    }

    case "passive_start_of_combat_attack": {
      // La Voz Nueva: random friendly unit gets +X Attack until end of combat.
      const attackBonus = hero.powerConfig.attack || 0;
      if (playerBoard.length > 0 && attackBonus > 0) {
        const idx = Math.floor(Math.random() * playerBoard.length);
        const target = playerBoard[idx];
        if (target) {
          target.attack += attackBonus;
          events.push({
            type: "hero_effect",
            effectId: "start_of_combat_attack_buff",
            details: { unitId: target.instanceId!, amount: attackBonus }
          });
        }
      }
      break;
    }

    // Other pre-combat passive cases can be added here in the future.
  }

  return events;
}

/**
 * Applies hero powers that require enemy board context.
 * This is called during combat simulation after boards are copied.
 * @param playerBoard The player's board (mutable copy for combat).
 * @param enemyBoard The enemy's board (for scouting/counting).
 * @returns An array of CombatEvent objects representing the effects.
 */
export function applyPreCombatHeroPassivesWithEnemyContext(playerBoard: Card[], enemyBoard: Card[]): CombatEvent[] {
  const hero = getCurrentHero();
  const events: CombatEvent[] = [];

  if (!hero) {
    return events;
  }

  switch (hero.powerType) {
    case "passive_start_of_combat_scout": {
      // El Esquinero: Gain +1 Attack per enemy unit
      const enemyCount = enemyBoard.length;
      const attackPerEnemy = hero.powerConfig.attack || 0;

      if (enemyCount > 0 && attackPerEnemy > 0) {
        // Apply to each player unit
        const affectedUnits: string[] = [];
        for (const unit of playerBoard) {
          if (unit) {
            unit.attack += attackPerEnemy * enemyCount;
            affectedUnits.push(unit.instanceId!);
          }
        }

        if (affectedUnits.length > 0) {
          events.push({
            type: "hero_effect",
            effectId: "scout_attack_buff",
            details: { unitIds: affectedUnits, totalBonus: attackPerEnemy * enemyCount, enemyCount }
          });
        }
      }
      break;
    }

    case "passive_start_of_combat_enemy_debuff": {
      // El del Apagon: cut the power on the other band's loudest unit.
      // First power in the game that mutates the ENEMY board outside of
      // damage. Attack is floored at 0 so a debuff can never hand the
      // opponent a negative-attack unit that heals on contact.
      const amount = hero.powerConfig.attack || 0;
      if (amount > 0 && enemyBoard.length > 0) {
        let target = enemyBoard[0];
        for (const unit of enemyBoard) {
          if (unit && (!target || unit.attack > target.attack)) target = unit;
        }
        if (target && target.attack > 0) {
          const applied = Math.min(amount, target.attack);
          target.attack -= applied;
          events.push({
            type: "hero_effect",
            effectId: "start_of_combat_enemy_debuff",
            details: { targetId: target.instanceId!, attack: -applied },
          });
        }
      }
      break;
    }
    case "passive_start_of_combat_damage": {
      // El del Slam: leftmost player unit deals X damage to leftmost enemy unit
      const damage = hero.powerConfig.damage || 0;
      if (playerBoard.length > 0 && enemyBoard.length > 0 && damage > 0) {
        const source = playerBoard[0];
        const target = enemyBoard[0];
        if (source && target) {
          target.health -= damage;
          events.push({
            type: "hero_effect",
            effectId: "start_of_combat_damage",
            details: {
              sourceId: source.instanceId!,
              targetId: target.instanceId!,
              damage
            }
          });
        }
      }
      break;
    }
  }

  return events;
}

/**
 * Applies hero power effects when the player buys a unit.
 * @param board The player's current board (for targeting buffs).
 * @param boughtCard The card that was just purchased (for tribal checks, etc.).
 */
export function applyOnBuyHeroPower(board: (Card | null)[], boughtCard: Card): void {
  const hero = getCurrentHero();
  if (!hero || hero.powerType !== "passive_on_buy") {
    return;
  }

  const attackBonus = hero.powerConfig.attack || 0;
  const healthBonus = hero.powerConfig.health || 0;
  if (attackBonus === 0 && healthBonus === 0) return;

  // Who receives the stats is read off powerConfig, never off hero.id. The
  // three branches that used to live here were keyed to guitar_teacher,
  // barrio_godfather and club_owner — all three have since been re-rolled
  // onto other powers, so each was a live buff waiting to be inherited by a
  // character whose card never claimed it. Same failure the refresh
  // discount had.
  const target =
    hero.powerConfig.target === 'bought'
      ? board.find(c => c && c.instanceId === boughtCard.instanceId) ?? null
      : pickRandom(board);
  if (!target) return;

  target.attack += attackBonus;
  target.health += healthBonus;
  console.log(`${hero.name} power: ${target.name} gained +${attackBonus}/+${healthBonus}!`);
}

/** Random occupied slot, or null on an empty board. */
function pickRandom(board: (Card | null)[]): Card | null {
  const units = board.filter(c => c !== null) as Card[];
  if (units.length === 0) return null;
  return units[Math.floor(Math.random() * units.length)];
}

/**
 * Applies hero powers that trigger at the end of the player's turn (before combat).
 * @param board The player's current board.
 * @param playerHealth The player's current health (mutable reference).
 * @returns Updated player health (for Scene Guardian healing).
 */
export function applyEndOfTurnHeroPowers(
  board: (Card | null)[],
  playerHealth: number,
  /** Gold still in hand as the turn ends. Bank-flavoured powers read this;
      omitted by callers that predate them, which simply never trigger. */
  unspentGold = 0,
): number {
  const hero = getCurrentHero();
  if (!hero) {
    return playerHealth;
  }

  switch (hero.powerType) {
    case "passive_unspent_gold_invest": {
      // Don Tandas: pay into the pot instead of spending, and the whole crew
      // grows — permanently, on the real board (unlike the burn powers above,
      // which only ever touch a combat copy). This is the bank half of the
      // roster axis in one effect.
      const threshold = hero.powerConfig.goldThreshold ?? 0;
      const atk = hero.powerConfig.attack || 0;
      const hp = hero.powerConfig.health || 0;
      if (unspentGold >= threshold) {
        let buffed = 0;
        for (const unit of board) {
          if (!unit) continue;
          unit.attack += atk;
          unit.health += hp;
          buffed++;
        }
        if (buffed > 0) {
          console.log(`${hero.name} power: banked ${unspentGold}g -> +${atk}/+${hp} to ${buffed} unit(s).`);
        }
      }
      break;
    }
    case "passive_healing": {
      // Scene Guardian: Heal your hero for 1 at end of turn
      const healAmount = hero.powerConfig.amount || 0;
      if (healAmount > 0) {
        playerHealth += healAmount;
        console.log(`${hero.name} power: Healed for ${healAmount}. Health: ${playerHealth}`);
      }
      break;
    }

    case "passive_end_of_turn": {
      // Block Chef: Give all units +1 Health at end of turn
      const healthBonus = hero.powerConfig.health || 0;
      if (healthBonus > 0) {
        const validUnits = board.filter(c => c !== null) as Card[];
        for (const unit of validUnits) {
          unit.health += healthBonus;
        }
        console.log(`${hero.name} power: All units gained +${healthBonus} Health!`);
      }
      break;
    }

    case "passive_summon_token": {
      // Block Captain: Summon a 1/1 token if you have 4 or fewer units
      const maxUnits = hero.powerConfig.maxUnits || 4;
      const validUnits = board.filter(c => c !== null) as Card[];

      if (validUnits.length <= maxUnits && validUnits.length < board.length) {
        const attack = hero.powerConfig.attack || 1;
        const health = hero.powerConfig.health || 1;

        // Find first empty slot
        const emptyIndex = board.findIndex(c => c === null);
        if (emptyIndex !== -1) {
          // Create a token unit
          const token: Card = {
            id: 'token',
            name: 'Token',
            faction: 'neutral',
            attack,
            health,
            tier: 1,
            text: 'Token unit',
            tags: ['token'],
            instanceId: `token_${Date.now()}_${Math.random()}`,
          };
          board[emptyIndex] = token;
          console.log(`${hero.name} power: Summoned a ${attack}/${health} token!`);
        }
      }
      break;
    }
  }

  return playerHealth;
}

/**
 * Applies post-combat hero powers that buff units based on combat performance.
 * @param board The player's current board.
 * @param damageDealers Set of instance IDs of units that dealt damage in combat.
 */
export function applyPostCombatHeroPowers(board: (Card | null)[], damageDealers: Set<string>): void {
  const hero = getCurrentHero();
  if (!hero) {
    return;
  }

  switch (hero.powerType) {
    case "passive_on_damage_dealt": {
      // El Veterano de la Lata: Units that dealt damage gain +1 Attack permanently
      const attackBonus = hero.powerConfig.attack || 0;
      if (attackBonus > 0 && damageDealers.size > 0) {
        for (const unit of board) {
          if (unit && damageDealers.has(unit.instanceId!)) {
            unit.attack += attackBonus;
            console.log(`${hero.name} power: ${unit.name} gained +${attackBonus} Attack for dealing damage!`);
          }
        }
      }
      break;
    }

    case "passive_on_combat_end_buff": {
      // El Tagger: random friendly unit gets +X/+Y permanently after each combat
      const attackBonus = hero.powerConfig.attack || 0;
      const healthBonus = hero.powerConfig.health || 0;
      const validUnits = board.filter(c => c !== null) as Card[];
      if (validUnits.length > 0 && (attackBonus > 0 || healthBonus > 0)) {
        const target = validUnits[Math.floor(Math.random() * validUnits.length)];
        target.attack += attackBonus;
        target.health += healthBonus;
        console.log(`${hero.name} power: ${target.name} gained +${attackBonus}/+${healthBonus} permanently!`);
      }
      break;
    }
  }
}

/**
 * Stubs for new street-lane hero powers that require infrastructure not yet built.
 * These are intentionally not yet wired up — the hero data references the power type
 * via the HeroPowerType enum, but the actual mechanic needs additional integration points:
 *
 * - `passive_loss_refresh` (El Chofer del Toquín)
 *   Needs: post-combat hook with win/loss outcome + per-turn refresh credit state
 *
 * - `passive_token_buff` (El Paletero)
 *   Needs: hook into the token summon path (tribes.ts breakpoints, summon abilities)
 *
 * - `passive_on_buy` with `firstOnly`/`permanent` config (La Bomba)
 *   Needs: per-turn first-buy tracking in game state
 *
 * Heroes with these powers will appear in the lineup but be no-ops at runtime until
 * the corresponding integration point is built. Tracked in the rename-pass branch.
 *
 * The active powers (`active_move_and_buff`, `active_stat_swap` + `buffAfter`,
 * `active_blood_price`, `active_crew_surge`) live in `heroPowerAdapter.ts`
 * instead — v1 wires actives through its own UI, and v2 resolves them through
 * the store. Nothing to stub here.
 */

/**
 * Applies hero powers that trigger at the start of the player's turn.
 * @param currentGold The player's current gold.
 * @returns Updated gold amount.
 */
export function applyStartOfTurnHeroPowers(currentGold: number): number {
  const hero = getCurrentHero();
  if (!hero) {
    return currentGold;
  }

  switch (hero.powerType) {
    case "passive_gold_generation": {
      // Festival Organizer: Gain 1 gold at start of turn (max 10)
      const goldAmount = hero.powerConfig.amount || 0;
      if (goldAmount > 0 && currentGold < 10) {
        const newGold = Math.min(currentGold + goldAmount, 10);
        console.log(`${hero.name} power: Gained ${newGold - currentGold} gold! (Total: ${newGold})`);
        return newGold;
      }
      break;
    }
  }

  return currentGold;
}

/**
 * Calculates the cost of a unit after applying hero power discounts.
 * Should be called when displaying unit costs and when purchasing units.
 * @param baseCost The base cost of the unit.
 * @param isFirstPurchase Whether this is the first unit purchased this turn.
 * @returns The adjusted cost after hero power effects.
 */
export function getUnitCostAfterHeroPower(baseCost: number, isFirstPurchase: boolean): number {
  const hero = getCurrentHero();
  if (!hero) {
    return baseCost;
  }

  switch (hero.powerType) {
    case "passive_shop_discount": {
      // Record Store Owner: First unit each turn costs 1 less (minimum 0)
      if (isFirstPurchase) {
        const discount = hero.powerConfig.discount || 0;
        return Math.max(0, baseCost - discount);
      }
      break;
    }
  }

  return baseCost;
}

/**
 * Checks if the hero has an active power that can freeze shop units.
 * This should be called in the shop UI to enable/disable the freeze functionality.
 * @returns True if the hero can freeze shop units.
 */
export function canFreezeShopUnits(): boolean {
  const hero = getCurrentHero();
  return hero?.powerType === "active_freeze_shop_unit";
}

/**
 * NOTE: The following hero powers require shop scene integration:
 *
 * 1. passive_shop_discount (Record Store Owner):
 *    - Track if first unit has been purchased this turn
 *    - Apply discount using getUnitCostAfterHeroPower() when buying units
 *    - Reset the purchase counter at start of each turn
 *
 * 2. active_freeze_shop_unit (Detail Specialist):
 *    - Add freeze functionality to shop UI
 *    - Store frozen unit IDs in game state
 *    - Prevent frozen units from being replaced during refresh
 *    - Clear frozen status when unit is purchased
 *    - Cost: 1 gold (hero.cost)
 */